import { useIntl } from 'react-intl';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { ListItems } from '~/eds/src/components/ListItems/ListItems';
import { PasswordInput } from '~/eds/src/components/PasswordInput/PasswordInput';
import { replaceAllSpaces } from '~/utils/strings';

import { ValidationResults } from './types';

interface Props {
  password: string;
  setPassword: (value: string) => void;
  confirmPassword: string;
  setConfirmPassword: (value: string) => void;
  isWorkdayUser: boolean;
  validationResult: ValidationResults;
}

export const LoginPassword = ({
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  validationResult,
  isWorkdayUser,
}: Props) => {
  const intl = useIntl();
  const iconStatus = ({
    password,
    validation,
  }: {
    password: string;
    validation: boolean;
  }) => {
    if (validation) {
      return {
        icon: 'check' as const,
        iconColor: 'status.success' as const,
      };
    } else {
      return {
        icon: 'x' as const,
        iconColor: password
          ? ('status.danger' as const)
          : ('status.info' as const),
      };
    }
  };

  return (
    <Layout w="100%" direction="column">
      <FormField<string, false>
        name="new password"
        input={PasswordInput}
        label={intl.formatMessage({
          id: 'LoginPassword.newPasswordLabel',
          defaultMessage: 'New password',
          description: 'Label for new password input field',
        })}
        disabled={isWorkdayUser}
        value={password}
        onChange={(password) => {
          if (!password) {
            setPassword('');
          } else {
            const trimPassword = replaceAllSpaces(password);
            setPassword(trimPassword);
          }
        }}
      />
      <ListItems
        mode="compact"
        as="ul"
        items={[
          {
            title: intl.formatMessage({
              id: 'LoginPassword.min15Chars',
              defaultMessage: 'Minimum of 15 characters',
              description: 'Password requirement: minimum of 15 characters',
            }),
            ...iconStatus({
              password,
              validation:
                validationResult.validations.satisfiesMinimumCharacters,
            }),
          },
          {
            title: intl.formatMessage({
              id: 'LoginPassword.capitalLetter',
              defaultMessage: 'At least one capital letter (A-Z)',
              description: 'Password requirement: at least one capital letter',
            }),
            ...iconStatus({
              password,
              validation:
                validationResult.validations.satisfiesCapitalCharacter,
            }),
          },
          {
            title: intl.formatMessage({
              id: 'LoginPassword.lowercaseLetter',
              defaultMessage: 'At least one lowercase letter (a-z)',
              description:
                'Password requirement: at least one lowercase letter',
            }),
            ...iconStatus({
              password,
              validation:
                validationResult.validations.satisfiesLowercaseCharacter,
            }),
          },
          {
            title: intl.formatMessage({
              id: 'LoginPassword.number',
              defaultMessage: 'At least one number (0-9)',
              description: 'Password requirement: at least one number',
            }),
            ...iconStatus({
              password,
              validation: validationResult.validations.satisfiesNumberCharacter,
            }),
          },
          {
            title: intl.formatMessage({
              id: 'LoginPassword.specialChar',
              defaultMessage: 'At least one special character',
              description:
                'Password requirement: at least one special character',
            }),
            ...iconStatus({
              password,
              validation:
                validationResult.validations.satisfiesSpecialCharacter,
            }),
          },
          {
            title: intl.formatMessage({
              id: 'LoginPassword.noIdenticalRun',
              defaultMessage: 'No more than 3 identical characters in a row',
              description:
                'Password requirement: no identical run of characters',
            }),
            ...iconStatus({
              password,
              validation: validationResult.validations.satisfiesNoIdenticalRun,
            }),
          },
          {
            title: intl.formatMessage({
              id: 'LoginPassword.noSequentialRun',
              defaultMessage:
                'No more than 3 consecutive characters in a sequence',
              description:
                'Password requirement: no sequential run of characters',
            }),
            ...iconStatus({
              password,
              validation: validationResult.validations.satisfiesNoSequentialRun,
            }),
          },
          {
            title: intl.formatMessage({
              id: 'LoginPassword.noGuessableInfo',
              defaultMessage:
                'Password cannot contain something easily guessable like your name, email address, or company name',
              description: 'Password requirement: no guessable information',
            }),
            icon: 'info' as const,
          },
        ]}
      />
      <FormField<string, false>
        name="confirm password"
        input={PasswordInput}
        label={intl.formatMessage({
          id: 'LoginPassword.confirmPasswordLabel',
          defaultMessage: 'Confirm password',
          description: 'Label for confirm password input field',
        })}
        disabled={isWorkdayUser}
        value={confirmPassword}
        onChange={(password) => {
          if (!password) {
            setPassword('');
          } else {
            const trimPassword = replaceAllSpaces(password);
            setConfirmPassword(trimPassword);
          }
        }}
        success={
          validationResult.isMatching && confirmPassword
            ? intl.formatMessage({
                id: 'LoginPassword.passwordsMatch',
                defaultMessage: 'Passwords match',
                description: 'Success message when passwords match',
              })
            : undefined
        }
        footer={
          !confirmPassword || validationResult.isMatching
            ? undefined
            : intl.formatMessage({
                id: 'LoginPassword.passwordsDoNotMatch',
                defaultMessage: 'Passwords do not match',
                description: 'Error message when passwords do not match',
              })
        }
      />
    </Layout>
  );
};
